import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useExam } from '../../contexts/ExamContext';
import { useOwnership } from '../../contexts/OwnershipContext';

const LoadingContext = createContext();

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

export const LoadingProvider = ({ children }) => {
  const { user } = useAuth();
  const { selectedExam, loading: examLoading } = useExam();
  const { loaded: ownershipLoaded } = useOwnership();
  
  const [criticalDataLoaded, setCriticalDataLoaded] = useState(false);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Track loading progress
  useEffect(() => {
    let progress = 0;
    if (user) progress += 25;
    if (selectedExam) progress += 25;
    if (ownershipLoaded) progress += 25;
    if (!examLoading) progress += 25;
    
    setLoadingProgress(progress);
    
    // Critical data is loaded when user, exam, and ownership are available
    if (user && selectedExam && ownershipLoaded && !examLoading) {
      setCriticalDataLoaded(true);
    }
    
    // All data is loaded when everything is ready
    if (user && selectedExam && ownershipLoaded && !examLoading) {
      setAllDataLoaded(true);
    }
  }, [user, selectedExam, ownershipLoaded, examLoading]);

  const value = {
    criticalDataLoaded,
    allDataLoaded,
    loadingProgress,
    isLoading: !allDataLoaded
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};

// Progressive loading component
export const ProgressiveLoader = ({ 
  children, 
  fallback = null, 
  criticalFallback = null,
  showProgress = false 
}) => {
  const { criticalDataLoaded, allDataLoaded, loadingProgress } = useLoading();

  if (!criticalDataLoaded) {
    return criticalFallback || (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div>Loading critical data...</div>
        {showProgress && (
          <div style={{ 
            width: '200px', 
            height: '4px', 
            backgroundColor: '#f0f0f0', 
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${loadingProgress}%`,
              height: '100%',
              backgroundColor: '#1976d2',
              transition: 'width 0.3s ease'
            }} />
          </div>
        )}
      </div>
    );
  }

  if (!allDataLoaded) {
    return fallback || (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading additional data...</div>
      </div>
    );
  }

  return children;
}; 